import {
  ColumnTable,
  Button,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Badge } from 'reactstrap';

import { PathsLayouts } from '../../../../config/routes';
import { TranslationsKeys } from '../../../../locales/translations';
import { closeAcademicOffer, deleteAcademicOffer } from '../api';
import {
  CeAcademicOffer,
  CeAcademicOfferStatus,
  ContinuousEducationType,
} from '../types';

/**
 * Obtiene el primer AcademicOfferStudyPlanVersionOffer de la oferta
 */
const getFirstAospvo = (offer: CeAcademicOffer) => {
  return offer.academicOfferStudyPlanVersionOffers?.[0];
};

/**
 * Obtiene el tipo de programa (COURSE/PROGRAM) desde CeStudyPlan
 */
const getProgramType = (
  offer: CeAcademicOffer,
): ContinuousEducationType | null => {
  const aospvo = getFirstAospvo(offer);
  return (
    aospvo?.studyPlanVersionOffer?.studyPlanVersion?.studyPlan?.studyPlanCe
      ?.type ?? null
  );
};

/**
 * Obtiene el texto del tipo de programa
 */
const getProgramTypeText = (
  programType: ContinuousEducationType | null,
  t: (key: string) => string,
): string => {
  if (!programType) return '-';
  const texts = {
    [ContinuousEducationType.COURSE]: t('table.programType.course'),
    [ContinuousEducationType.PROGRAM_COURSE]: t('table.programType.program'),
  };
  return texts[programType] || '-';
};

/**
 * Obtiene el tipo de proceso desde admissions -> subprocess -> process -> tuitionProcessType
 */
const getProcessTypeName = (offer: CeAcademicOffer): string | null => {
  const aospvo = getFirstAospvo(offer);
  const admissions = aospvo?.admissions;
  if (!admissions || admissions.length === 0) return null;
  return admissions[0]?.subprocess?.process?.tuitionProcessType?.name ?? null;
};

/**
 * Obtiene el total de inscritos
 */
const getEnrolledCount = (offer: CeAcademicOffer): number | string => {
  return offer.enrolled ?? 0;
};

const getBadgeColor = (status: CeAcademicOfferStatus) =>
  ({
    [CeAcademicOfferStatus.Borrador]: '#ffe6a1',
    [CeAcademicOfferStatus.Activa]: '#CEF0DA',
    [CeAcademicOfferStatus.Cerrada]: '#F2C0BD',
  }[status] ?? 'secondary');

function StatusBadgeDropdown({
  offer,
  t,
  onClose,
}: {
  offer: CeAcademicOffer;
  t: (key: string) => string;
  onClose: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const labels = {
    [CeAcademicOfferStatus.Borrador]: t('table.status.draft'),
    [CeAcademicOfferStatus.Activa]: t('table.status.active'),
    [CeAcademicOfferStatus.Cerrada]: t('table.status.closed'),
  };

  const canClose = offer.status === CeAcademicOfferStatus.Activa;

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <Badge
        className="status-badge fs-16 fw-400 py-2 px-4 text-dark"
        style={{
          backgroundColor: getBadgeColor(offer.status),
          width: '110px',
          cursor: canClose ? 'pointer' : 'default',
        }}
        pill
        onClick={() => canClose && setIsOpen(!isOpen)}
      >
        {labels[offer.status] || offer.status}
      </Badge>
      {isOpen && canClose && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 999 }}
            onClick={() => setIsOpen(false)}
          />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              marginTop: '5px',
              minWidth: '110px',
              backgroundColor: '#fff',
              borderRadius: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                backgroundColor: '#F2C0BD',
                textAlign: 'center',
              }}
              onClick={() => {
                setIsOpen(false);
                onClose();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = 'brightness(0.95)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = 'none';
              }}
            >
              {labels[CeAcademicOfferStatus.Cerrada]}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export const useColumns = (options?: { onDelete?: () => void }) => {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const history = useHistory();

  const handleDelete = useCallback(
    (row: CeAcademicOffer) => {
      showDialogConfirm({
        icon: {
          color: 'warning',
          name: 'warning',
        },
        title: t('messages.deleteConfirm.title'),
        subtitle: t('messages.deleteConfirm.subtitle'),
        btnClose: {
          text: t('form.cancel'),
        },
        btnConfirm: {
          text: t('messages.deleteConfirm.confirm'),
          onConfirm: async () => {
            const { error } = await deleteAcademicOffer(row.id);
            if (error) {
              addToast({
                color: 'danger',
                icon: 'error',
                text: t('messages.deleteError'),
              });
              return;
            }
            addToast({
              color: 'success',
              icon: 'success',
              text: t('messages.deleteSuccess'),
            });
            options?.onDelete?.();
          },
        },
      });
    },
    [t, options],
  );

  const handleClose = useCallback(
    (row: CeAcademicOffer) => {
      showDialogConfirm({
        icon: {
          color: 'warning',
          name: 'warning',
        },
        title: t('messages.closeConfirm.title'),
        subtitle: t('messages.closeConfirm.subtitle'),
        btnClose: {
          text: t('form.cancel'),
        },
        btnConfirm: {
          text: t('messages.closeConfirm.confirm'),
          onConfirm: async () => {
            const { error } = await closeAcademicOffer(row.id);
            if (error) {
              addToast({
                color: 'danger',
                icon: 'error',
                text: t('messages.closeError'),
              });
              return;
            }
            addToast({
              color: 'success',
              icon: 'success',
              text: t('messages.closeSuccess'),
            });
            options?.onDelete?.();
          },
        },
      });
    },
    [t, options],
  );

  const columns = useMemo<ColumnTable<CeAcademicOffer>[]>(() => {
    return [
      {
        columnName: 'processType',
        headerText: t('table.headers.processType'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => getProcessTypeName(row) ?? '-',
      },
      {
        columnName: 'programType',
        headerText: t('table.headers.programType'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => getProgramTypeText(getProgramType(row), t),
      },
      {
        columnName: 'name',
        headerText: t('table.headers.name'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return (
            aospvo?.studyPlanVersionOffer?.studyPlanVersion?.studyPlan?.name ??
            '-'
          );
        },
      },
      {
        columnName: 'modality',
        headerText: t('table.headers.modality'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return (
            aospvo?.studyPlanVersionOffer?.studyPlanVersion?.studyPlan?.modality
              ?.name ?? '-'
          );
        },
      },
      {
        columnName: 'campus',
        headerText: t('table.headers.campus'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return aospvo?.studyPlanVersionOffer?.campus?.name ?? '-';
        },
      },
      {
        columnName: 'startDate',
        headerText: t('table.headers.startDate'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return aospvo?.startDate ?? '-';
        },
      },
      {
        columnName: 'endDate',
        headerText: t('table.headers.endDate'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return aospvo?.endDate ?? '-';
        },
      },
      {
        columnName: 'quota',
        headerText: t('table.headers.quota'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const aospvo = getFirstAospvo(row);
          return aospvo?.quota ?? '-';
        },
      },
      {
        columnName: 'enrolled',
        headerText: t('table.headers.enrolled'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => getEnrolledCount(row),
      },
      {
        columnName: 'status',
        headerText: t('table.headers.status'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => (
          <StatusBadgeDropdown
            offer={row}
            t={t}
            onClose={() => handleClose(row)}
          />
        ),
      },
      {
        columnName: 'actions',
        headerText: t('table.headers.actions'),
        thClassName: 'text-center',
        tdClassName: 'd-flex gap-2 justify-content-center align-items-center',
        cellFormat: ({ row }) => (
          <>
            {row.status === CeAcademicOfferStatus.Borrador && (
              <Button
                text={t('table.actions.delete')}
                outlined
                size="sm"
                className="mx-2"
                onClick={() => handleDelete(row)}
              />
            )}
            {row.status !== CeAcademicOfferStatus.Cerrada && (
              <Button
                text={t('table.actions.edit')}
                size="sm"
                onClick={() =>
                  history.push(
                    `${PathsLayouts.continuousEducation}/academic-offers/${row.id}/wizard`,
                  )
                }
              />
            )}
          </>
        ),
      },
    ];
  }, [t, history, handleDelete, handleClose]);

  return columns;
};
